'use client';

import { useEffect, useMemo, useState } from 'react';
import Link from 'next/link';
import styled from 'styled-components';
import AddToCart from './AddToCart';
import { statics } from '@/data/store';
import { COLORS } from '@/constants/colors';
import { formatMakeName, deduplicateModels } from '@/utils/formatters';

const MOBILE_BREAKPOINT = 600;

interface ApiState {
  year: string;
  make_id: string;
  model_name: string;
  engine: string;
}

// One row per model+engine fitment, as the API returns them. The same
// physical model (e.g. "Silverado 1500") can show up under several
// differently-formatted `name` values ("Silverado 1500", "silverado-1500",
// "silverado_1500", ...) left over from past catalog imports, but always
// shares one consistent `slug` - so slug is what we group/dedupe on, not
// name. See handleModelSelect/engineOptions below.
interface ModelRow {
  name: string;
  title: string;
  slug?: string;
  displacement?: string;
  engine?: string;
  intake?: string;
  description?: string;
  createdAt?: string;
}

interface ApiData {
  make?: Array<{ id: string; title: string }>;
  name?: ModelRow[];
}

interface Product {
  id: string;
  gfu_part_num: string;
  title: string;
  price?: string;
  buy_url: string;
  images?: Array<{ link: string }>;
  product_type_id?: number;
}

// Sort products so that product_type_id 13 (Pedal Power Kit) appears last
function sortProductsWithPedalPowerLast(products: Product[]): Product[] {
  return [...products].sort((a, b) => {
    const aIsPedalPower = a.product_type_id === 13;
    const bIsPedalPower = b.product_type_id === 13;
    if (aIsPedalPower && !bIsPedalPower) return 1;
    if (!aIsPedalPower && bIsPedalPower) return -1;
    return 0;
  });
}

function compare(a: any, b: any, key: string) {
  if (a[key] < b[key]) return -1;
  if (a[key] > b[key]) return 1;
  return 0;
}

function useIsMobile() {
  // Start with false to match SSR, then update after hydration
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    // Set initial value after mount
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT);
    };

    // Check immediately after mount
    checkMobile();

    // Add resize listener
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  return isMobile;
}

export default function PerfectFilter() {
  const isMobile = useIsMobile();
  const [tabValue, setTabValue] = useState(0);
  const [apiStr, setApiStr] = useState<ApiState>({
    year: '',
    make_id: '',
    model_name: '',
    engine: '',
  });
  const [apiData, setApiData] = useState<ApiData>({});
  // Which model the user picked, tracked by its (consistent) slug rather
  // than the raw `name` value the API happens to use - see ModelRow above.
  const [selectedModelSlug, setSelectedModelSlug] = useState('');
  const [searchPartNo, setSearchPartNo] = useState('');
  const [productDetails, setProductDetails] = useState<Product[] | null>(null);

  const handleChange = (key: 'year' | 'make_id', value: string) => {
    const tempArr: (keyof ApiState)[] = ['year', 'make_id', 'model_name', 'engine'];
    const index = tempArr.indexOf(key);
    tempArr.splice(0, index + 1);

    let tempData = { ...apiData };
    let tempStr = { ...apiStr };

    tempArr.forEach((k) => {
      if (k === 'make_id') delete tempData.make;
      else if (k === 'model_name') delete tempData.name;
      tempStr[k] = '';
    });

    setProductDetails(null);
    setSelectedModelSlug('');
    setApiData(tempData);
    setApiStr({ ...tempStr, [key]: value });
  };

  // Every row returned for a given year+make is one model+engine fitment,
  // already carrying its own displacement/engine/intake - so once that
  // list is loaded, picking a model and an engine are both just client-side
  // filters over it. No extra round trip, and no risk of a second fetch
  // exact-matching a `model_name` string that turns out to be one of the
  // several inconsistent duplicates for the model the user actually wants
  // (the "missing engine options" bug this replaced).
  const handleModelSelect = (slug: string) => {
    setProductDetails(null);
    setSelectedModelSlug(slug);
    setApiStr((prev) => ({ ...prev, model_name: '', engine: '' }));
  };

  const engineOptions = useMemo(() => {
    if (!selectedModelSlug || !Array.isArray(apiData.name)) return [];
    const rows = apiData.name.filter((row) => row.slug === selectedModelSlug);
    const seen = new Set<string>();
    const deduped: ModelRow[] = [];
    for (const row of rows) {
      const key = `${row.displacement}|${row.engine}|${row.intake}`;
      if (!seen.has(key)) {
        seen.add(key);
        deduped.push(row);
      }
    }
    return deduped.sort((a, b) => compare(a, b, 'displacement'));
  }, [apiData.name, selectedModelSlug]);

  const handleEngineSelect = (displacement: string) => {
    setProductDetails(null);
    if (!displacement) {
      setApiStr((prev) => ({ ...prev, model_name: '', engine: '' }));
      return;
    }
    // More than one duplicate row can share this exact displacement (same
    // physical engine listed under a couple of the model's name variants) -
    // prefer the most recently added catalog entry as the canonical one to
    // search products against.
    const candidates = (apiData.name || []).filter(
      (row) => row.slug === selectedModelSlug && row.displacement === displacement
    );
    const canonical = candidates.slice().sort((a, b) => {
      const aTime = a.createdAt ? Date.parse(a.createdAt) : 0;
      const bTime = b.createdAt ? Date.parse(b.createdAt) : 0;
      return bTime - aTime;
    })[0];

    setApiStr((prev) => ({
      ...prev,
      engine: displacement,
      model_name: canonical?.name || '',
    }));
  };

  // Fetch data based on selections - only year and make ever trigger a
  // network call; model and engine are derived client-side (see above).
  useEffect(() => {
    const runFetch = async () => {
      const params = new URLSearchParams();
      if (apiStr.year) params.append('year', apiStr.year);
      if (apiStr.make_id) params.append('make_id', apiStr.make_id);

      const url = `${statics.BaseUrl}/product-search?${params.toString()}`;
      try {
        const res = await fetch(url);
        const response = await res.json();

        if (response?.data?.length > 0) {
          const filteredData = response.data as any[];
          filteredData.sort((a, b) => compare(a, b, 'name'));
          const objectKey: keyof ApiData = apiStr.make_id ? 'name' : 'make';
          setApiData((prev) => ({ ...prev, [objectKey]: filteredData }));
        }
      } catch (err) {
        console.error(err);
      }
    };

    if (apiStr.year) runFetch();
  }, [apiStr.year, apiStr.make_id]);

  const handleFindMatching = async () => {
    setProductDetails(null);

    if (tabValue === 1 && searchPartNo.trim()) {
      try {
        const params = new URLSearchParams({
          gfu_part_num: searchPartNo.trim(),
          is_active: '1',
        });
        const res = await fetch(`${statics.BaseUrl}/product-search?${params.toString()}`);
        const data = await res.json();
        setProductDetails(sortProductsWithPedalPowerLast(data?.data || []));
      } catch (err) {
        console.error(err);
      }
      return;
    }

    if (tabValue === 0 && apiStr.year && apiStr.make_id && apiStr.model_name && apiStr.engine) {
      try {
        const params = new URLSearchParams();
        Object.entries(apiStr).forEach(([key, value]) => {
          if (value) params.append(key, value);
        });
        const res = await fetch(`${statics.BaseUrl}/product-search?${params.toString()}`);
        const data = await res.json();
        setProductDetails(sortProductsWithPedalPowerLast(data?.data || []));
      } catch (err) {
        console.error(err);
      }
    }
  };

  const generateYearOptions = () => {
    const arr = [];
    const endYear = new Date().getFullYear() + 1; // include next model year
    for (let i = endYear; i >= 1959; i--) {
      arr.push(
        <option key={i} value={String(i)}>
          {i}
        </option>
      );
    }
    return arr;
  };

  const vehicleReady = apiStr.year && apiStr.make_id && apiStr.model_name && apiStr.engine;
  const partReady = tabValue === 1 && searchPartNo.trim().length > 0;
  const canSearch = tabValue === 0 ? vehicleReady : partReady;

  const makeDisplay =
    Array.isArray(apiData.make) && apiStr.make_id
      ? formatMakeName(apiData.make.find((m) => String(m.id) === String(apiStr.make_id))?.title)
      : null;

  return (
    <Container>
      <Title>Find the Perfect Filter for Your Vehicle</Title>

      <TabsContainer>
        <TabButton
          $active={tabValue === 0}
          onClick={() => setTabValue(0)}
          type="button"
        >
          {isMobile ? 'Vehicle Search' : 'Find by Vehicle'}
        </TabButton>
        <TabButton
          $active={tabValue === 1}
          onClick={() => setTabValue(1)}
          type="button"
        >
          {isMobile ? 'Part No.' : 'Find by Part Number'}
        </TabButton>
      </TabsContainer>

      {tabValue === 0 && (
        <FormRow>
          <SelectWrapper>
            <FieldLabel>Year</FieldLabel>
            <SelectBox>
              <StyledSelect
                value={apiStr.year || ''}
                onChange={(e) => handleChange('year', e.target.value)}
                aria-label="Year"
              >
                <option value="">Select Year</option>
                {generateYearOptions()}
              </StyledSelect>
            </SelectBox>
          </SelectWrapper>

          <SelectWrapper>
            <FieldLabel>Make</FieldLabel>
            <SelectBox>
              <StyledSelect
                value={apiStr.make_id || ''}
                onChange={(e) => handleChange('make_id', e.target.value)}
                disabled={!apiStr.year}
                aria-label="Make"
              >
                <option value="">{makeDisplay || 'Select Make'}</option>
                {Array.isArray(apiData.make) &&
                  apiData.make.map((item) => (
                    <option key={item.id} value={String(item.id)}>
                      {formatMakeName(item.title)}
                    </option>
                  ))}
              </StyledSelect>
            </SelectBox>
          </SelectWrapper>

          <SelectWrapper>
            <FieldLabel>Model</FieldLabel>
            <SelectBox>
              <StyledSelect
                value={selectedModelSlug}
                onChange={(e) => handleModelSelect(e.target.value)}
                disabled={!apiStr.make_id}
                aria-label="Model"
              >
                <option value="">Select Model</option>
                {deduplicateModels(apiData.name).map((m) => (
                  <option key={m.slug || m.name} value={m.slug || m.name}>
                    {m.title}
                  </option>
                ))}
              </StyledSelect>
            </SelectBox>
          </SelectWrapper>

          <SelectWrapper>
            <FieldLabel>Engine</FieldLabel>
            <SelectBox>
              <StyledSelect
                value={apiStr.engine || ''}
                onChange={(e) => handleEngineSelect(e.target.value)}
                disabled={!selectedModelSlug}
                aria-label="Engine"
              >
                <option value="">Select Engine</option>
                {engineOptions.map((eng) => (
                  <option key={`${eng.displacement}-${eng.engine}-${eng.intake}`} value={eng.displacement}>
                    {eng.displacement}
                  </option>
                ))}
              </StyledSelect>
            </SelectBox>
          </SelectWrapper>
        </FormRow>
      )}

      {tabValue === 1 && (
        <PartNumberRow>
          <StyledInput
            type="text"
            placeholder="Enter part number"
            value={searchPartNo}
            onChange={(e) => setSearchPartNo(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleFindMatching()}
          />
        </PartNumberRow>
      )}

      <FooterRow>
        <SearchButton
          onClick={handleFindMatching}
          disabled={!canSearch}
          type="button"
        >
          Find Matching Filters
        </SearchButton>
      </FooterRow>

      {productDetails && (
        <ResultsSection>
          {productDetails.length === 0 ? (
            <p style={{ color: '#aaa', margin: 0 }}>No products found. Try different selections.</p>
          ) : (
            productDetails.map((product, key) => {
              const selectedEngine =
                apiStr?.engine && engineOptions.length
                  ? engineOptions.find((eng) => eng.displacement === apiStr.engine) || engineOptions[0]
                  : null;

              const partTitle = product.title || `Green Filter Part #${product.gfu_part_num || ''}`;
              const imageSrc =
                product.images && product.images.length
                  ? product.images[0].link
                  : 'https://via.placeholder.com/150';

              return (
                <ProductCard key={product.id || product.gfu_part_num}>
                  <ProductCardImage>
                    <img src={imageSrc} alt={partTitle} loading="lazy" />
                  </ProductCardImage>
                  <ProductCardBody>
                    <div>
                      <ProductCardTitle>{partTitle}</ProductCardTitle>
                      <SpecList>
                        {apiStr?.year && (
                          <p>
                            <strong>Year:</strong> {apiStr.year}
                          </p>
                        )}
                        {selectedEngine && (
                          <>
                            <p>
                              <strong>Engine:</strong> {selectedEngine.engine}
                            </p>
                            <p>
                              <strong>Disp:</strong> {selectedEngine.displacement}
                            </p>
                            <p>
                              <strong>Intake:</strong> {selectedEngine.intake}
                            </p>
                            <p>
                              <strong>Fitment Note:</strong>{' '}
                              {selectedEngine.description ? `${selectedEngine.description} Models` : 'All Models'}
                            </p>
                          </>
                        )}
                        {!selectedEngine && (
                          <p>
                            <strong>Fitment Note:</strong> All Models
                          </p>
                        )}
                      </SpecList>
                    </div>

                    {/* Single Shopify component with custom layout */}
                    <ProductActionsWrapper>
                      {product.gfu_part_num ? (
                        <ViewDetailsButton
                          href={`/store/filter/${product.gfu_part_num}`}
                        >
                          View Product Details
                          <svg
                            width="18"
                            height="18"
                            viewBox="0 0 24 24"
                            fill="currentColor"
                            aria-hidden="true"
                            style={{ marginLeft: 4 }}
                          >
                            <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z" />
                          </svg>
                        </ViewDetailsButton>
                      ) : (
                        <ViewDetailsButton href="#" style={{ opacity: 0.7, cursor: 'not-allowed', pointerEvents: 'none' as const }}>
                          View Product Details
                        </ViewDetailsButton>
                      )}

                      {/* Single Shopify Add to Cart component */}
                      <ShopifyWrapper>
                        <AddToCart
                          buyButtonId={product?.buy_url}
                          id={`${product?.id?.toString()}-${key.toString()}`}
                          background={COLORS.brandGreen}
                          color="#fff"
                          hidePrice={false}
                          priceSize="18px"
                        />
                      </ShopifyWrapper>
                    </ProductActionsWrapper>
                  </ProductCardBody>
                </ProductCard>
              );
            })
          )}
        </ResultsSection>
      )}
    </Container>
  );
}

// Styled Components
const Container = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  background: linear-gradient(145deg, #1a1a1a 0%, #2a2a2a 100%);
  border: 2px solid #444;
  border-radius: 12px;
  padding: 20px 24px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.08);

  @media (max-width: 767px) {
    padding: 16px 16px 18px;
    max-width: none;
    box-sizing: border-box;
  }
`;

const Title = styled.h2`
  margin: 0 0 12px;
  font-family: Lato, sans-serif;
  font-size: 20px;
  font-weight: 600;
  color: #fff;

  @media (max-width: 767px) {
    font-size: 17px;
    text-align: center;
    margin-bottom: 10px;
  }
`;

const TabsContainer = styled.div`
  display: flex;
  gap: 32px;
  margin-bottom: 16px;
  position: relative;
  border-bottom: 3px solid #333;
`;

const TabButton = styled.button<{ $active: boolean }>`
  padding: 12px 0;
  font-family: Lato, sans-serif;
  font-size: 15px;
  font-weight: 600;
  background: transparent;
  border: none;
  color: ${props => props.$active ? COLORS.brandGreen : '#999'};
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  white-space: nowrap;

  &:hover {
    color: ${props => props.$active ? COLORS.brandGreen : '#bbb'};
  }

  &::after {
    content: '';
    position: absolute;
    bottom: -3px;
    left: 0;
    right: 0;
    height: 3px;
    background: ${props => props.$active ? COLORS.brandGreen : 'transparent'};
    transition: background 0.3s ease;
  }

  @media (max-width: 767px) {
    font-size: 14px;
    padding: 10px 0;
  }
`;

/* Horizontal on every breakpoint: a 2-column grid on phones (compact,
   2 rows tall) that opens up to a single 4-column row from tablet width
   up, so the fields always spread sideways rather than stacking. */
const FormRow = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  margin-bottom: 14px;

  @media (max-width: 767px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 10px;
    margin-bottom: 12px;
  }
`;

const SelectWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
`;

const FieldLabel = styled.span`
  font-family: Lato, sans-serif;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  color: #999;
`;

const SelectBox = styled.div`
  position: relative;

  &::after {
    content: '▼';
    position: absolute;
    right: 14px;
    top: 50%;
    transform: translateY(-50%);
    color: #999;
    pointer-events: none;
    font-size: 10px;
  }

  @media (max-width: 767px) {
    &::after {
      right: 10px;
      font-size: 9px;
    }
  }
`;

const StyledSelect = styled.select`
  width: 100%;
  padding: 12px 34px 12px 14px;
  font-family: Lato, sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: #aaa;
  background-color: #2a2a2a;
  border: 2px solid #555;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &:hover {
    border-color: #777;
  }

  @media (max-width: 767px) {
    padding: 10px 28px 10px 12px;
    font-size: 13px;
  }

  &:focus {
    outline: none;
    border-color: ${COLORS.brandGreen};
    color: #fff;
  }

  &:disabled {
    background-color: #1a1a1a;
    border-color: #444;
    color: #666;
    cursor: not-allowed;
    opacity: 0.6;
  }

  /* Styling for when a value is selected */
  &:not([value=""]) {
    color: #fff;
  }

  option {
    background-color: #3a3a3a;
    color: #fff;
    padding: 14px 16px;
    font-family: Lato, sans-serif;
    font-size: 15px;
    font-weight: 400;
    line-height: 1.6;
  }

  option:first-child {
    color: #aaa;
  }

  option:hover {
    background-color: #4a4a4a;
  }

  option:checked {
    background-color: #4a4a4a;
  }
`;

const PartNumberRow = styled.div`
  margin-bottom: 14px;
`;

const StyledInput = styled.input`
  width: 100%;
  max-width: 320px;
  padding: 10px 14px;
  font-family: Lato, sans-serif;
  font-size: 14px;
  color: #fff;
  background-color: #2a2a2a;
  border: 2px solid #666;
  border-radius: 8px;
  transition: border-color 0.2s ease;

  &:hover {
    border-color: #888;
  }

  &:focus {
    outline: none;
    border-color: ${COLORS.brandGreen};
  }

  &::placeholder {
    color: #bbb;
  }
`;

const FooterRow = styled.div`
  display: flex;
  align-items: center;
  margin-top: 14px;
  flex-wrap: wrap;
  gap: 12px;
`;

const SearchButton = styled.button`
  padding: 12px 32px;
  font-family: Lato, sans-serif;
  font-size: 15px;
  font-weight: 700;
  color: #fff;
  background-color: ${COLORS.brandGreen};
  border: none;
  border-radius: 8px;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0, 173, 35, 0.3);
  transition: background-color 0.2s ease;

  &:hover:not(:disabled) {
    background-color: #00c728;
  }

  &:disabled {
    background-color: #2a2a2a;
    color: #777;
    border: 2px solid #444;
    cursor: not-allowed;
    box-shadow: none;
  }
`;

const ResultsSection = styled.div`
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid #333;
`;

const ProductCard = styled.div`
  display: flex;
  align-items: stretch;
  background: #fff;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);

  @media (max-width: 600px) {
    flex-direction: column;
    margin-bottom: 12px;
  }
`;

const ProductCardImage = styled.div`
  flex-shrink: 0;
  width: 160px;
  min-height: 140px;
  background: #f5f5f5;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  overflow: hidden;

  img {
    width: 100%;
    height: auto;
    max-height: 140px;
    object-fit: contain;
    display: block;
  }

  /* Fallback for broken images */
  img[src=""],
  img:not([src]),
  img[src="https://via.placeholder.com/150"] {
    min-width: 80px;
    min-height: 80px;
    background: #e0e0e0;
    border-radius: 4px;
  }

  @media (max-width: 600px) {
    width: 100%;
    min-height: 100px;
    padding: 10px;

    img {
      max-height: 100px;
    }
  }
`;

const ProductCardBody = styled.div`
  flex: 1;
  padding: 20px 24px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  font-family: Lato, sans-serif;

  @media (max-width: 600px) {
    padding: 12px 14px;
  }
`;

const ProductCardTitle = styled.h3`
  margin: 0 0 12px;
  font-size: 20px;
  font-weight: 700;
  color: #333;
  line-height: 1.3;

  @media (max-width: 600px) {
    font-size: 18px;
    margin-bottom: 8px;
    line-height: 1.25;
  }
`;

const SpecList = styled.div`
  margin: 0 0 16px;
  font-size: 13px;
  color: #333;
  line-height: 1.45;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 4px 12px;
  align-items: start;

  p {
    margin: 0;
    min-width: 0;
  }

  strong {
    font-weight: 600;
    margin-right: 4px;
  }

  @media (max-width: 600px) {
    margin-bottom: 10px;
    font-size: 12px;
    line-height: 1.4;
    grid-template-columns: 1fr 1fr;
    gap: 2px 10px;
  }
`;

const BUTTON_HEIGHT_DESKTOP = 40;

const ProductActionsWrapper = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  align-items: end;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 8px;
  }
`;

const ShopifyWrapper = styled.div`
  width: 100%;

  /* Style the Shopify component container */
  > div {
    display: grid;
    grid-template-columns: 1fr;
    gap: 12px;

    /* Dropdown and price on top */
    > div:not(:last-child) {
      width: 100%;
    }

    /* Button takes full width in its column */
    button {
      width: 100%;
      height: ${BUTTON_HEIGHT_DESKTOP}px;
      min-height: ${BUTTON_HEIGHT_DESKTOP}px;
    }
  }
`;

const ViewDetailsButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 0 16px;
  height: ${BUTTON_HEIGHT_DESKTOP}px;
  min-height: ${BUTTON_HEIGHT_DESKTOP}px;
  box-sizing: border-box;
  background-color: transparent;
  color: ${COLORS.brandGreen} !important;
  font-family: Lato, sans-serif;
  font-size: 13px;
  font-weight: 600;
  text-decoration: none;
  border-radius: 4px;
  border: 2px solid ${COLORS.brandGreen};
  cursor: pointer;
  width: 100%;
  transition: background-color 0.2s, color 0.2s;

  &:hover {
    background-color: rgba(0, 173, 35, 0.08);
    color: ${COLORS.brandGreen} !important;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;
